/**
 * NeuNexus Energy — Native Slide Renderer
 * Every slide is drawn in code (React + SVG). No PDF images.
 * Colour system:
 *   background #04070E   surface rgba(20,255,140,0.04)
 *   neon       #14FF8C   soft-mint #7DD3A9
 *   accents per layer  (blue Siemens, green VCOW, amber DFOPTIX)
 */
import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, Cpu, Eye, Sparkles, Cable, ShieldCheck, Bus, Building2, Leaf } from "lucide-react";

const NEON = "#14FF8C";
const MINT = "#7DD3A9";
const INK  = "#E4F1FF";
const DIM  = "rgba(228,241,255,0.62)";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

const Eyebrow = ({ children, accent = NEON }) => (
  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: accent }}>
    {children}
  </div>
);

const H1 = ({ children }) => (
  <h1 className="font-bold tracking-tight text-white text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.08]">
    {children}
  </h1>
);

const Lede = ({ children }) => (
  <p className="text-[14px] sm:text-[15.5px] leading-[1.65] max-w-[62ch]" style={{ color: DIM }}>
    {children}
  </p>
);

// -------------------------------------------------- Slide 1 · Cover
const CoverSlide = ({ c }) => (
  <div className="relative min-h-[560px] flex flex-col justify-between p-8 lg:p-14"
    style={{ background: "radial-gradient(1200px 700px at 12% 8%, rgba(20,255,140,0.14) 0%, transparent 65%), #04070E" }}>
    <motion.div {...fade(0)}>
      <Eyebrow>{c.eyebrow}</Eyebrow>
    </motion.div>

    <motion.div {...fade(0.15)} className="max-w-4xl">
      <div className="inline-block mb-6 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.3em]"
        style={{ border: `1px solid ${NEON}55`, background: `${NEON}12`, color: NEON }}>
        NeuNexus × Siemens × VCOW × DFOPTIX
      </div>
      <h1 className="font-bold tracking-tight text-white text-[48px] sm:text-[68px] lg:text-[92px] leading-[0.98]">
        {c.title}
      </h1>
      <p className="mt-6 text-[16px] sm:text-[19px] leading-[1.55] max-w-[62ch]" style={{ color: INK }}>
        {c.subtitle}
      </p>
    </motion.div>

    <motion.div {...fade(0.3)} className="flex items-center justify-between flex-wrap gap-4 pt-8 border-t" style={{ borderColor: `${NEON}22` }}>
      <div className="text-[10.5px] uppercase tracking-[0.28em]" style={{ color: MINT }}>{c.tag}</div>
      <div className="flex items-center gap-4 text-[10.5px] uppercase tracking-[0.22em]" style={{ color: MINT }}>
        <span>Uetze</span><span className="opacity-40">/</span>
        <span>Niedersachsen</span><span className="opacity-40">/</span>
        <span>Deutschland</span>
      </div>
    </motion.div>
  </div>
);

// -------------------------------------------------- Slide 2 · Challenge
const ChallengeSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow accent="#FF6B6B">{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
      {c.points.map((p, i) => (
        <motion.div key={i} {...fade(0.3 + i * 0.08)}
          className="p-5 rounded-md flex items-start gap-4"
          style={{ background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.28)" }}>
          <div className="shrink-0 font-bold text-[24px] leading-none" style={{ color: "#FF6B6B" }}>{p.k}</div>
          <div className="text-[13.5px] leading-snug" style={{ color: INK }}>{p.v}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

// -------------------------------------------------- Slide 3 · Location (Uetze)
const LocationSlide = ({ c }) => (
  <div className="grid lg:grid-cols-[1.1fr_1fr] min-h-[560px]">
    <div className="p-8 lg:p-14">
      <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
      <dl className="mt-8 space-y-3">
        {c.facts.map((f, i) => (
          <motion.div key={i} {...fade(0.3 + i * 0.06)} className="flex items-center justify-between gap-6 py-3 border-b"
            style={{ borderColor: `${NEON}18` }}>
            <dt className="text-[11px] uppercase tracking-[0.24em]" style={{ color: MINT }}>{f.k}</dt>
            <dd className="text-[13.5px] text-right" style={{ color: INK }}>{f.v}</dd>
          </motion.div>
        ))}
      </dl>
    </div>
    <div className="relative p-8 lg:p-10 flex items-center justify-center border-l" style={{ borderColor: `${NEON}18`, background: "rgba(20,255,140,0.02)" }}>
      {/* Stylised Germany + Uetze location */}
      <svg viewBox="0 0 400 460" className="w-full max-w-[420px]">
        <defs>
          <radialGradient id="uetGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={NEON} stopOpacity="0.55" />
            <stop offset="60%" stopColor={NEON} stopOpacity="0.08" />
            <stop offset="100%" stopColor={NEON} stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Germany silhouette (stylised) */}
        <path
          d="M180 32 Q210 22 240 40 Q265 52 260 78 Q290 92 300 118 Q320 138 306 168 Q328 188 316 218 Q334 248 310 280 Q322 314 292 336 Q300 372 262 388 Q240 418 200 420 Q158 424 140 396 Q108 400 96 366 Q70 352 76 320 Q56 296 74 264 Q58 234 84 210 Q68 178 96 156 Q82 128 116 108 Q108 74 148 60 Q158 40 180 32 Z"
          fill="rgba(20,255,140,0.05)"
          stroke={NEON}
          strokeOpacity="0.4"
          strokeWidth="1.2"
        />
        {/* Rivers hint */}
        <path d="M120 200 Q180 220 240 240 T360 260" fill="none" stroke={MINT} strokeOpacity="0.25" strokeDasharray="3 4" />
        {/* Berlin dot */}
        <circle cx="278" cy="180" r="3" fill={MINT} opacity="0.65" />
        <text x="285" y="184" fontSize="9" fill={MINT} opacity="0.7" fontFamily="Inter">Berlin</text>
        {/* Hannover dot */}
        <circle cx="190" cy="178" r="3" fill={MINT} opacity="0.65" />
        <text x="130" y="172" fontSize="9" fill={MINT} opacity="0.7" fontFamily="Inter">Hannover</text>
        {/* Uetze — main marker */}
        <circle cx="205" cy="176" r="60" fill="url(#uetGlow)" />
        <circle cx="205" cy="176" r="7" fill={NEON} />
        <circle cx="205" cy="176" r="12" fill="none" stroke={NEON} strokeWidth="1.5">
          <animate attributeName="r" from="12" to="26" dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.9" to="0" dur="2.5s" repeatCount="indefinite" />
        </circle>
        <text x="220" y="172" fontSize="14" fontWeight="700" fill={NEON} fontFamily="Inter">UETZE</text>
        <text x="220" y="188" fontSize="9" fill={MINT} fontFamily="Inter" letterSpacing="2">SMART FACTORY</text>
      </svg>
    </div>
  </div>
);

// -------------------------------------------------- Slide 4 · KPI grid
const KpisSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {c.kpis.map((k, i) => (
        <motion.div key={i} {...fade(0.28 + i * 0.06)}
          className="relative p-6 rounded-lg overflow-hidden"
          style={{ background: "rgba(20,255,140,0.05)", border: `1px solid ${NEON}28` }}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-20"
            style={{ background: `radial-gradient(circle, ${NEON} 0%, transparent 60%)` }} />
          <div className="relative">
            <div className="font-bold text-[38px] leading-none" style={{ color: NEON }}>{k.v}</div>
            <div className="mt-3 text-[13px] font-semibold text-white">{k.l}</div>
            <div className="mt-1 text-[11px]" style={{ color: MINT }}>{k.s}</div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

// -------------------------------------------------- Slide 5,7,9 · Layer (partner)
const LayerSlide = ({ c, partner, accent }) => {
  const icon = partner === "Siemens" ? <Zap size={22} /> : partner === "VCOW" ? <Cpu size={22} /> : <Eye size={22} />;
  return (
    <div className="grid lg:grid-cols-[1fr_0.9fr] min-h-[560px]">
      <div className="p-8 lg:p-14">
        <motion.div {...fade(0)}><Eyebrow accent={accent}>{c.eyebrow}</Eyebrow></motion.div>
        <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
        <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
        <div className="mt-8 space-y-3">
          {c.pillars.map((p, i) => (
            <motion.div key={i} {...fade(0.3 + i * 0.07)}
              className="flex items-start gap-4 p-4 rounded-md"
              style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${accent}22` }}>
              <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-[13px] font-bold"
                style={{ background: `${accent}18`, border: `1px solid ${accent}55`, color: accent }}>
                {i + 1}
              </div>
              <div>
                <div className="text-[14px] font-semibold text-white">{p.t}</div>
                <div className="text-[12.5px] mt-1 leading-snug" style={{ color: DIM }}>{p.d}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="relative flex items-center justify-center p-8 lg:p-10 border-l"
        style={{ borderColor: `${accent}22`, background: `radial-gradient(500px 400px at 60% 40%, ${accent}18 0%, transparent 65%)` }}>
        <motion.div {...fade(0.15)}
          className="w-[240px] h-[240px] rounded-full flex flex-col items-center justify-center text-center"
          style={{ border: `1.5px solid ${accent}`, background: `${accent}0d`, boxShadow: `0 0 80px -10px ${accent}66` }}>
          <div style={{ color: accent }}>{icon}</div>
          <div className="mt-3 font-bold text-[26px] tracking-tight text-white">{partner}</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.28em]" style={{ color: accent }}>
            Layer {c.eyebrow.match(/\d+/)?.[0] || ""}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// -------------------------------------------------- Slide 6 · Digital Twin
const TwinSlide = ({ c, accent }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow accent={accent}>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
      {c.stages.map((s, i) => (
        <motion.div key={i} {...fade(0.28 + i * 0.08)}
          className="relative p-5 rounded-md"
          style={{ background: "rgba(0,165,255,0.04)", border: `1px solid ${accent}30` }}>
          <div className="text-[10px] uppercase tracking-[0.28em] mb-3" style={{ color: accent }}>
            0{i + 1}
          </div>
          <div className="text-[14px] font-semibold text-white">{s.t}</div>
          <div className="mt-2 text-[12px] leading-snug" style={{ color: DIM }}>{s.d}</div>
          {i < c.stages.length - 1 && (
            <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px" style={{ background: `${accent}55` }} />
          )}
        </motion.div>
      ))}
    </div>
  </div>
);

// -------------------------------------------------- Slide 8 · Redundancy rings
const RedundancySlide = ({ c, accent }) => (
  <div className="grid lg:grid-cols-[1fr_1fr] min-h-[560px]">
    <div className="p-8 lg:p-14">
      <motion.div {...fade(0)}><Eyebrow accent={accent}>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
      <div className="mt-8 space-y-3">
        {c.rings.map((r, i) => (
          <motion.div key={i} {...fade(0.28 + i * 0.08)} className="flex items-center gap-4 p-4 rounded-md"
            style={{ background: "rgba(20,255,140,0.04)", border: `1px solid ${accent}30` }}>
            <ShieldCheck size={18} style={{ color: accent }} />
            <div>
              <div className="text-[13.5px] font-semibold text-white">{r.t}</div>
              <div className="text-[12px]" style={{ color: DIM }}>{r.d}</div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {c.standards.map((s) => (
          <span key={s} className="text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full"
            style={{ background: `${accent}12`, border: `1px solid ${accent}40`, color: accent }}>
            {s}
          </span>
        ))}
      </div>
    </div>
    <div className="relative flex items-center justify-center border-l"
      style={{ borderColor: `${accent}20`, background: `radial-gradient(500px 400px at 50% 50%, ${accent}18 0%, transparent 65%)` }}>
      <svg viewBox="0 0 300 300" className="w-full max-w-[380px]">
        <circle cx="150" cy="150" r="120" fill="none" stroke={accent} strokeOpacity="0.9" strokeWidth="1.5" />
        <circle cx="150" cy="150" r="88"  fill="none" stroke={accent} strokeOpacity="0.6" strokeWidth="1.5" strokeDasharray="4 4" />
        <circle cx="150" cy="150" r="56"  fill="none" stroke={accent} strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="2 6" />
        <circle cx="150" cy="150" r="6"   fill={accent} />
        <text x="150" y="30"  fontSize="12" fontWeight="700" fill={accent} fontFamily="Inter" textAnchor="middle">RING A</text>
        <text x="150" y="66"  fontSize="12" fontWeight="700" fill={accent} fontFamily="Inter" textAnchor="middle" opacity="0.8">RING B</text>
        <text x="150" y="100" fontSize="12" fontWeight="700" fill={accent} fontFamily="Inter" textAnchor="middle" opacity="0.55">RING C</text>
        <circle cx="150" cy="30"  r="4" fill={accent} />
        <circle cx="270" cy="150" r="4" fill={accent} />
        <circle cx="150" cy="270" r="4" fill={accent} />
        <circle cx="30"  cy="150" r="4" fill={accent} />
      </svg>
    </div>
  </div>
);

// -------------------------------------------------- Slide 10 · Precision leap
const LeapSlide = ({ c, accent }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow accent={accent}>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 rounded-lg overflow-hidden" style={{ border: `1px solid ${accent}30` }}>
      <div className="grid grid-cols-[1.2fr_1fr_1fr] px-6 py-3 text-[10px] uppercase tracking-[0.24em]"
        style={{ background: `${accent}12`, color: accent }}>
        <div>Metric</div>
        <div className="text-center">Classical</div>
        <div className="text-center">DFOPTIX</div>
      </div>
      {c.rows.map((r, i) => (
        <motion.div key={i} {...fade(0.28 + i * 0.06)}
          className="grid grid-cols-[1.2fr_1fr_1fr] px-6 py-4 items-center border-t"
          style={{ borderColor: `${accent}18`, background: i % 2 ? "rgba(255,255,255,0.015)" : "transparent" }}>
          <div className="text-[13.5px] text-white font-medium">{r.m}</div>
          <div className="text-[13px] text-center" style={{ color: DIM }}>{r.a}</div>
          <div className="text-[13px] text-center font-semibold" style={{ color: accent }}>{r.b}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

// -------------------------------------------------- Slide 11 · Calibration facility
const FacilitySlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
      {c.cards.map((card, i) => (
        <motion.div key={i} {...fade(0.28 + i * 0.08)}
          className="p-6 rounded-lg"
          style={{ background: "rgba(20,255,140,0.04)", border: `1px solid ${NEON}22` }}>
          <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
            style={{ background: `${NEON}12`, border: `1px solid ${NEON}44`, color: NEON }}>
            <Sparkles size={16} />
          </div>
          <div className="text-[15px] font-semibold text-white">{card.t}</div>
          <div className="mt-2 text-[12.5px] leading-snug" style={{ color: DIM }}>{card.d}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

// -------------------------------------------------- Slide 12 · Flow (ecosystem)
const FlowSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-14 relative">
      <svg viewBox="0 0 960 260" className="w-full">
        <defs>
          <linearGradient id="flowLine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00A5FF" />
            <stop offset="45%" stopColor={NEON} />
            <stop offset="80%" stopColor="#FFB800" />
            <stop offset="100%" stopColor={NEON} />
          </linearGradient>
        </defs>
        <path d="M100 130 Q245 40 385 130 T685 130 T880 130" fill="none" stroke="url(#flowLine)" strokeWidth="2" strokeDasharray="6 6" opacity="0.85">
          <animate attributeName="stroke-dashoffset" from="0" to="24" dur="1.4s" repeatCount="indefinite" />
        </path>
        {c.nodes.map((n, i) => {
          const cx = 120 + i * 240;
          const colors = ["#00A5FF", NEON, "#FFB800", NEON];
          const col = colors[i];
          return (
            <g key={i}>
              <circle cx={cx} cy={130} r={44} fill="#04070E" stroke={col} strokeWidth="2" />
              <circle cx={cx} cy={130} r={58} fill="none" stroke={col} strokeOpacity="0.25" />
              <text x={cx} y={124} fontSize="14" fontWeight="700" fill="#fff" fontFamily="Inter" textAnchor="middle">{n.t}</text>
              <text x={cx} y={220} fontSize="9.5" fill={col} fontFamily="Inter" textAnchor="middle" letterSpacing="1.5">{n.s.toUpperCase()}</text>
            </g>
          );
        })}
      </svg>
    </div>
  </div>
);

// -------------------------------------------------- Slide 13 · Mobility hub
const MobilitySlide = ({ c }) => (
  <div className="grid lg:grid-cols-[1fr_0.9fr] min-h-[560px]">
    <div className="p-8 lg:p-14">
      <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
      <ul className="mt-8 space-y-3">
        {c.bullets.map((b, i) => (
          <motion.li key={i} {...fade(0.28 + i * 0.07)} className="flex items-start gap-4 p-4 rounded-md"
            style={{ background: "rgba(20,255,140,0.04)", border: `1px solid ${NEON}22` }}>
            <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: `${NEON}18`, color: NEON }}>
              {[<Bus size={14} key="a"/>, <Cable size={14} key="b"/>, <Building2 size={14} key="c"/>, <Leaf size={14} key="d"/>][i] || <Sparkles size={14} />}
            </div>
            <div className="text-[13.5px] leading-snug" style={{ color: INK }}>{b}</div>
          </motion.li>
        ))}
      </ul>
    </div>
    <div className="relative flex items-center justify-center border-l p-8"
      style={{ borderColor: `${NEON}18`, background: `radial-gradient(500px 400px at 50% 50%, ${NEON}14 0%, transparent 65%)` }}>
      <svg viewBox="0 0 360 320" className="w-full max-w-[420px]">
        {/* Hub circle */}
        <circle cx="180" cy="160" r="52" fill="none" stroke={NEON} strokeWidth="1.5" />
        <circle cx="180" cy="160" r="8"  fill={NEON} />
        <text x="180" y="164" fontSize="10" fontWeight="700" fill="#04070E" fontFamily="Inter" textAnchor="middle">HUB</text>
        {/* Rays to services */}
        {[[70,60,"Hannover"],[290,60,"Braunschweig"],[60,260,"Peine"],[300,260,"Wolfsburg"]].map(([x,y,label],i) => (
          <g key={i}>
            <line x1="180" y1="160" x2={x} y2={y} stroke={NEON} strokeOpacity="0.35" strokeDasharray="3 4" />
            <circle cx={x} cy={y} r="5" fill={MINT} />
            <text x={x} y={y - 10} fontSize="10" fill={MINT} fontFamily="Inter" textAnchor="middle">{label}</text>
          </g>
        ))}
      </svg>
    </div>
  </div>
);

// -------------------------------------------------- Slide 14 · Economics (bar chart)
const EconomicsSlide = ({ c }) => {
  const max = Math.max(...c.bars.map((b) => b.v));
  return (
    <div className="p-8 lg:p-14 min-h-[560px]">
      <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

      <div className="mt-10 rounded-lg p-6" style={{ background: "rgba(20,255,140,0.03)", border: `1px solid ${NEON}22` }}>
        <div className="text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: MINT }}>{c.yLabel}</div>
        <div className="flex items-end gap-3" style={{ height: 240 }}>
          {c.bars.map((b, i) => {
            const h = Math.round((b.v / max) * 200);  // px height, max bar = 200px
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-2" style={{ height: "100%" }}>
                <div className="text-[10px] font-semibold" style={{ color: NEON }}>{b.v}</div>
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: h }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.06, ease: "easeOut" }}
                  className="w-full rounded-t-md"
                  style={{ background: `linear-gradient(180deg, ${NEON} 0%, ${NEON}55 100%)`, boxShadow: `0 0 22px -6px ${NEON}88` }}
                />
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: MINT }}>{b.l}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {c.notes.map((n, i) => (
          <div key={i} className="text-[12px] p-3 rounded-md" style={{ background: "rgba(20,255,140,0.05)", border: `1px solid ${NEON}22`, color: INK }}>
            {n}
          </div>
        ))}
      </div>
    </div>
  );
};

// -------------------------------------------------- Slide 15 · CTA / next steps
const CtaSlide = ({ c }) => (
  <div className="grid lg:grid-cols-[1fr_1fr] min-h-[560px]">
    <div className="p-8 lg:p-14">
      <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

      <motion.a {...fade(0.5)} href={`mailto:${c.cta.detail.split(" · ")[0]}`}
        className="mt-10 inline-flex items-center gap-3 px-6 py-4 rounded-full font-bold text-[13px] uppercase tracking-[0.22em] transition hover:translate-y-[-2px]"
        style={{ background: NEON, color: "#04070E", boxShadow: `0 20px 60px -10px ${NEON}88` }}
        data-testid="neunexus-cta-button">
        {c.cta.label} <ArrowUpRight size={16} />
      </motion.a>
      <div className="mt-4 text-[11px] uppercase tracking-[0.22em]" style={{ color: MINT }}>
        {c.cta.detail}
      </div>
    </div>
    <div className="p-8 lg:p-14 border-l" style={{ borderColor: `${NEON}18`, background: "rgba(20,255,140,0.02)" }}>
      <div className="text-[10px] uppercase tracking-[0.28em] mb-6" style={{ color: MINT }}>Timeline</div>
      <ol className="relative border-l-2 pl-6 space-y-6" style={{ borderColor: `${NEON}44` }}>
        {c.timeline.map((t, i) => (
          <motion.li key={i} {...fade(0.28 + i * 0.1)} className="relative">
            <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full"
              style={{ background: NEON, boxShadow: `0 0 12px ${NEON}` }} />
            <div className="text-[11px] uppercase tracking-[0.24em]" style={{ color: NEON }}>{t.d}</div>
            <div className="mt-1 text-[14px] text-white">{t.t}</div>
          </motion.li>
        ))}
      </ol>
    </div>
  </div>
);

// -------------------------------------------------- Router
export function SlideRenderer({ slide, lang }) {
  const c = slide.i18n[lang] || slide.i18n.en;
  const accent = slide.accent || NEON;

  switch (slide.kind) {
    case "title":       return <CoverSlide c={c} />;
    case "challenge":   return <ChallengeSlide c={c} />;
    case "location":    return <LocationSlide c={c} />;
    case "kpis":        return <KpisSlide c={c} />;
    case "layer":       return <LayerSlide c={c} partner={slide.partner} accent={accent} />;
    case "twin":        return <TwinSlide c={c} accent={accent} />;
    case "redundancy":  return <RedundancySlide c={c} accent={accent} />;
    case "leap":        return <LeapSlide c={c} accent={accent} />;
    case "facility":    return <FacilitySlide c={c} />;
    case "flow":        return <FlowSlide c={c} />;
    case "mobility":    return <MobilitySlide c={c} />;
    case "economics":   return <EconomicsSlide c={c} />;
    case "cta":         return <CtaSlide c={c} />;
    default:            return <div className="p-14 text-white">Slide not implemented: {slide.kind}</div>;
  }
}
